import React, { useState, useEffect, useCallback } from 'react';
import { usePayment } from '../../context/PaymentContext';
import { useCurrency } from '../../context/CurrencyContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Alert,
} from '@mui/material';

export default function RevenueManagement() {
  const {
    commissionRates,
    updateCommissionRates,
    transactions,
    getAllTransactions,
  } = usePayment();
  const { currencyCode, formatCurrency } = useCurrency();

  const [newRates, setNewRates] = useState(commissionRates);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState({
    totalRevenue: 0,
    jobPostingRevenue: 0,
    serviceBookingRevenue: 0,
    subscriptionRevenue: 0,
  });

  const loadTransactions = useCallback(async () => {
    try {
      const transactionList = await getAllTransactions();
      calculateStats(transactionList);
    } catch (error) {
      setError('Failed to load transactions');
    }
  }, [getAllTransactions]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);


  const calculateStats = (transactionList) => {
    const stats = transactionList.reduce(
      (acc, transaction) => {
        const amount = transaction.commission || transaction.amount || 0;
        acc.totalRevenue += amount;

        // Normalize type aliases coming from backend
        const type = String(transaction.type || '').trim();
        const normalized = (
          type === 'job_posting' ? 'jobPosting' :
          type === 'service_booking' ? 'serviceBooking' :
          type === 'profile_boost' ? 'profileBoost' :
          type
        );

        if (normalized === 'jobPosting') {
          acc.jobPostingRevenue += amount;
        } else if (normalized === 'serviceBooking') {
          acc.serviceBookingRevenue += amount;
        } else if (normalized === 'premiumSubscription' || normalized === 'profileBoost') {
          // Count profile boost payments under subscription revenue bucket
          acc.subscriptionRevenue += amount;
        }

        return acc;
      },
      {
        totalRevenue: 0,
        jobPostingRevenue: 0,
        serviceBookingRevenue: 0,
        subscriptionRevenue: 0,
      }
    );

    setStats(stats);
  };

  const handleRateChange = (type, value) => {
    setNewRates(prev => ({
      ...prev,
      [type]: Number(value),
    }));
  };

  const handleUpdateRates = async () => {
    try {
      await updateCommissionRates(newRates);
      setSuccess('Commission rates updated successfully');
      setError('');
    } catch (error) {
      setError('Failed to update commission rates');
      setSuccess('');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Revenue Management
      </Typography>

      {/* Revenue Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Revenue
              </Typography>
              <Typography variant="h5">
                {formatCurrency(stats.totalRevenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Job Posting Revenue
              </Typography>
              <Typography variant="h5">
                {formatCurrency(stats.jobPostingRevenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Service Booking Revenue
              </Typography>
              <Typography variant="h5">
                {formatCurrency(stats.serviceBookingRevenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Subscription Revenue
              </Typography>
              <Typography variant="h5">
                {formatCurrency(stats.subscriptionRevenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Commission Rate Settings */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Commission Rate Settings
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Job Posting Commission (%)"
              type="number"
              value={newRates.jobPosting}
              onChange={(e) => handleRateChange('jobPosting', e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Service Booking Commission (%)"
              type="number"
              value={newRates.serviceBooking}
              onChange={(e) => handleRateChange('serviceBooking', e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label={`Premium Subscription Fee (${currencyCode})`}
              type="number"
              value={newRates.premiumSubscription}
              onChange={(e) => handleRateChange('premiumSubscription', e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateRates}
          >
            Update Rates
          </Button>
        </Box>
      </Paper>

      {/* Transaction History */}
      <Paper>
        <Typography variant="h6" sx={{ p: 2 }}>
          Recent Transactions
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Commission</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction._id || transaction.id || `${transaction.type}-${transaction.timestamp}`}>
                  <TableCell>{transaction.type}</TableCell>
                  <TableCell>{formatCurrency(transaction.amount)}</TableCell>
                  <TableCell>{formatCurrency(transaction.commission)}</TableCell>
                  <TableCell>{transaction.status}</TableCell>
                  <TableCell>
                    {new Date(transaction.timestamp).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}